import { StepType } from '@reactour/tour';
import findIndex from 'lodash.findindex';

export type TourStep = StepType & {
  // a unique identifier for the step, so we don't have directly refer to index of step.
  // otherwise if you change steps, you have to adjust the whole codebase.
  // use getTourStep function insted, ex: getTourStep(steps, '<uid>')
  uid: string;

  disablePrevButton?: boolean;

  disableNextButton?: boolean;

  // if true, nothing will happen when user click on highlighted area
  disableInteraction?: boolean;
};

export function getTourStep(steps: StepType[], uid: string): number {
  return findIndex(steps as TourStep[], { uid });
}

export const beerSteps: TourStep[] = [
  {
    uid: 'DeElspw',
    selector: '#step-beer-dashboard',
    content:
      "Welcome! We'll walk you through the steps for using our WebApp. Let's dive in!",
    disablePrevButton: true,
    disableInteraction: true,
    highlightedSelectors: ['*'],
  },
  {
    uid: '7LNXMXP',
    selector: '#step-profile-dropdown',
    content:
      'Clicking on the username gives you access to the settings and also allows you to restart this demo after closing or finishing it.',
    disableInteraction: true,
  },
  {
    uid: 'QJNtbat',
    selector: '#step-dashboard',
    content:
      'The "Dashboard" button is always available in the top bar and brings you back to this screen.',
    disableInteraction: true,
  },
  {
    uid: 'ACTWbyI',
    selector: '#step-support',
    content:
      'This button opens up your email program so that you can contact us in case you encounter any issues, or you have any questions.',
    disableInteraction: true,
  },
  {
    uid: 'DA5VzI8',
    selector: '#step-manual',
    content:
      'This link takes you to our BB Resources page, where you can find several useful materials on how to use our technology.',
    disableInteraction: true,
  },
  {
    uid: '3GXnbok',
    selector: '#step-beer-fermentation-tracking',
    content: 'The Dashboard is divided into "Fermentation Tracking"',
    disableInteraction: true,
  },
  {
    uid: '6j9mhLb',
    selector: '#step-beer-single-measurements”',
    content: 'and "Single Analysis"',
    disableInteraction: true,
  },
  {
    uid: 'tX6eJ0i',
    selector: '#step-beer-new-analysis',
    content:
      'First, we will walk you through how a single measurement is created. For this click on "Start New Analysis".',
    disableNextButton: true,
    position: 'bottom',
  },
  {
    uid: 'RLqQcrK',
    selector: '#step-new-viab-conz-create',
    resizeObservables: ['#step-new-viab-conz-create'],
    content:
      'Here, you can choose the type of measurement you wish to do. We will do a Viability and Concentration measurement so please click on "Viability and Concentration"',
    disablePrevButton: true,
    disableNextButton: true,
    padding: 30,
  },
  {
    uid: 'hxc1fUV',
    selector: '#step-camera-selection',
    resizeObservables: ['#step-camera-selection'],
    content:
      'This is the part where you normally need to have a microscope connected. You would then select the microscope camera in this dropdown.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'j5r7kRP',
    selector: '#step-image-upload-preview',
    content:
      'For the tutorial, however, we have already taken care of the image capture and provided you with 5 images.',
    disableInteraction: true,
  },
  {
    uid: 'whAJdvL',
    selector: '#step-information-on-sample',
    content: 'Next, we can add some information about the sample.',
    disableInteraction: true,
  },
  {
    uid: 'cBPkqlj',
    selector: '#step-sample-dilution',
    content:
      'Here, you put how the sample was diluted. If it was a yeast slurry, for example, you probably had to dilute 1 part sample with 99 parts water, so we have entered "99" for water. Feel free to edit these values.',
  },
  {
    uid: 'pEd7eqZ',
    selector: '#step-dilution-sample',
    content:
      'The diluted sample is almost always mixed in equal parts with the stain. So, you can leave the default values of 1 & 1 for the ratio here.',
  },
  {
    uid: 'QhxTv0P',
    selector: '#step-batch-name',
    content:
      'Optionally, you can give your sample a name, such as "Demo measurement" in this case.',
  },
  {
    uid: '1mddq44',
    selector: '#step-batch-description',
    content: 'and add any other notes you wish to save with your measurement.',
  },
  {
    uid: 'eBHmWhV',
    selector: '#step-batch-create-btn',
    content: 'Once all of this is done press "Analyze" to start the analysis.',
    disableNextButton: true,
  },
  {
    uid: '51oEgCY',
    selector: '#step-analysis-creation-done',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      "Just like that, we have the results of the analysis. That was easy, wasn't it?",
    disablePrevButton: true,
    position: 'center',
  },
  {
    uid: 'XCNgAve',
    selector: '#step-concentration',
    content:
      'Here, you see the concentration. Note, that this is the concentration before dilution (That is why we had to tell our app how much the sample was diluted)',
  },
  {
    uid: 'GMJl9x9',
    selector: '#step-budding',
    content:
      'This is the budding index. A budding index of 100% would mean that all cells are involved in a division (so either a mother or a daughter cell)',
  },
  {
    uid: 'lWhCkNk',
    selector: '#step-viability',
    content:
      'This value indicates the viability, so the percentage of cells that are alive. For pitching, this value should be above 90 or even better 95%',
  },
  {
    uid: 'nuMB1Bb',
    selector: '#step-analysis-general-info',
    content: 'Some general info about the analysis is shown here.',
  },
  {
    uid: 'QNNxXe8',
    selector: '#step-histogram',
    content:
      'There is also a histogram of the sizes of all yeast detected. Advanced users can use this to estimate the age of the population (as yeast gets larger with each division), or even to see if there are different strains of yeast present.',
  },
  {
    uid: 'miioOSA',
    selector: '.step-individual-image',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      'Finally, you can also review each individual image and see how it was analyzed. Click on the image to see a more detailed view.',
    disableNextButton: true,
  },
  {
    uid: 'yZgJOSQ',
    selector: '.step-image-comparison',
    resizeObservables: ['.step-image-comparison'],
    highlightedSelectors: ['step-image-comparison'],
    content:
      'If you move the slider left and right, you can compare the original image and the result of the automatic analysis.',
    disablePrevButton: true,
  },
  {
    uid: 'OJsmruk',
    selector: '.step-image-comparison',
    content:
      'Note, that cells touching white borders are always counted, and cells touching red borders are never counted. Just like how it works with the edges of a hemocytometer.',
  },
  {
    uid: 'WjZ6cqa',
    selector: '#step-edit-cells-btn',
    content:
      'It is also possible to make some manual corrections (such as adding or removing cells or changing classification), in case that is ever needed. Results would then of course be recomputed after making a change. Manual corrections are normally not needed. ',
    disableInteraction: true,
  },
  {
    uid: 'MlOWXEI',
    selector: '#step-edit-analysis',
    content:
      'In case you spot a mistake with the data entered for the analysis, such as entering the wrong dilution, you can click edit here to change this. The results would then also be computed again.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BfEDQcB',
    selector: '#step-download-pdf',
    resizeObservables: ['#step-download-pdf'],
    content: 'You can download a pdf report of this analysis.',
    disableInteraction: true,
  },
  {
    uid: 'venUIAF',
    selector: '#step-export-csv',
    content:
      'or export the full data as CSV so you can work with it in Excel for example.',
    disableInteraction: true,
  },
  {
    uid: 'DlEHbdj',
    selector: '#step-pitch-rate-btn',
    content:
      'Finally, now that we have the results, we can also calculate the pitch rate for a new batch by clicking on the "Pitch Calculator".',
    disableNextButton: true,
  },
  {
    uid: 'gOTK8sl',
    selector: '#step-pitch-settings-btn',
    resizeObservables: ['#step-pitch-settings-btn'],
    content:
      'The preferred type of units for the pitch calculator can be changed in the settings. We will stick with the defaults for now.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BA1Qa3z',
    selector: '#step-wort-volume',
    content:
      "Now, let's get started. The volume for the beer you will be making should be input here. We have entered 20 hl as an example.",
  },
  {
    uid: 'FzN3b0U',
    selector: '#step-gravity',
    content:
      'Next, add the degrees Plato you measured (or the specific gravity if you switched the preferred units). We have entered 12°P as an example.',
  },
  {
    uid: 'Ms3LgZC',
    selector: '#step-pitch-rate-input',
    content:
      'After adding the pitch rate here, the target concentration will be calculated. Recommended pitch rates for an Ale are usually in the range of 0.5 - 1 and for a Lager 1 - 1.2.',
  },
  {
    uid: 'zofpyzw',
    selector: '#step-target-concentration',
    content:
      'Here, it shows the calculated target concentration. Alternatively, if you already know your target concentration you can also ignore the fields above and input the value directly here.',
  },
  {
    uid: 'jaHrRaW',
    selector: '#step-pitch-rate-calc-btn',
    content:
      'After clicking "Calculate" you will get the exact volume or weight you should use to pitch.',
  },
  {
    uid: 'dzsFe6t',
    selector: '#step-dashboard',
    content:
      'Now let\'s go back to the Dashboard to show you some other options. Please click the "Dashboard" button.',
    disableNextButton: true,
  },
  {
    uid: 'luZY2o3',
    selector: '#step-beer-analysis-history-btn',
    content: 'click on "History Of Analysis"',
    disablePrevButton: true,
    disableNextButton: true,
  },
  {
    uid: 'GxV3pF1',
    selector: '#step-measurement-table',
    content:
      'This overview shows you all the analyses of this account, including the one we just did.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'O2KTUsd',
    selector: '#step-measurement-table',
    content:
      'Most users only see their own analyses. But special admin accounts can review measurements made by several users. This way a QA manager can review results from several sites for example.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: '8umJT2v',
    selector: '#step-measurement-table',
    content:
      'Clicking on any analysis will take you to a result screen just like what we saw before.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'Dm7QIsT',
    selector: '#step-dashboard',
    content: 'Lets go back to the Dashboard for now.',
    disableNextButton: true,
  },
  {
    uid: 'wuwV2GN',
    selector: '#step-beer-fermentation-tracking',
    content: "Now let's look at fermentation tracking.",
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: '7MTHe2U',
    selector: '#step-beer-fermentation-tracking',
    content:
      'If you want to measure yeast not as a one-off for pitching but instead to monitor a fermentation over time you can use this feature.',
    disableInteraction: true,
  },
  {
    uid: 'H51Pxou',
    selector: '#step-beer-fermentation-tracking',
    content:
      'Tracked fermentations can give you valuable insights, especially for determining optimal pitch rates for new recipes or determining how pitch rates have to be adapted between vessels with different brewing efficiencies.',
    disableInteraction: true,
  },
  {
    uid: 'GI0ZWgu',
    selector: '#step-beer-tracking-overview-btn',
    content:
      'Each measurement here is much the same as what we just did for a "Single Analysis". To see how this works let\'s go to the overview.',
    disableNextButton: true,
  },
  {
    uid: 'OkjY32s',
    selector: '#step-fermentation-overview',
    content:
      "We have prepared a completed fermentation for the demo, so let's open that now.",
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'prahZDR',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    content:
      'As you can see here, measurements over many days are combined into a graph so you can see how your fermentation progresses.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'iRFDckB',
    selector: '#step-fermentation-details',
    content:
      'Here you can review the details entered when initially creating a fermentation',
    disableInteraction: true,
    position: 'top',
  },
  {
    uid: 'UiDnvMp',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    mutationObservables: ['#step-fermentation-graph'],
    content:
      'and a timeline summarizing all the measurement data. You can modify the display of the graph in several ways:',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'etjvWdB',
    selector: '#step-fermentation-graph-list',
    content:
      'Single measurements can be excluded by clicking the checkboxes. Please test this now.',
    disableInteraction: false,
  },
  {
    uid: 'FBsyTDN',
    selector: '#step-fermentation-graph',
    content:
      'Individual lines in the graph can be turned on and off by clicking on the labels in the legend at the bottom. Please test this now.',
    disableInteraction: false,
    position: 'bottom',
  },
  {
    uid: 'mZKsxky',
    selector: '#step-fermentation-single-analysis',
    content:
      'Below the graph, the detailed results for each measurement that makes up a fermentation are displayed.',
    position: 'center',
    disableInteraction: true,
  },
  {
    uid: '7iC88l2',
    selector: '#step-fermentation-tabs',
    content:
      'By selecting the tabs here you can switch the results of which measurement is shown.',
  },
  {
    uid: 'BWbcFaA',
    selector: '#step-dashboard',
    content:
      "This should cover the basic knowledge about tracked fermentations, let's go back to the Dashboard.",
    disableInteraction: false,
    disableNextButton: true,
  },
  {
    uid: '3dIOlms',
    selector: '#step-beer-dashboard',
    content:
      "This concludes the guided tutorial and it has now been disabled (you can click on the username and restart it at any time). Feel free to continue to explore the web app on your own. If you have any questions don't hesitate to get in touch!",
    disableInteraction: true,
    disablePrevButton: true,
    disableNextButton: true,
    highlightedSelectors: ['*'],
    position: 'top',
  },
];

export const wineSteps: TourStep[] = [
  {
    uid: 'DeElspw',
    selector: '#step-wine-dashboard',
    content:
      "Welcome! We'll walk you through the steps for using our WebApp. Let's dive in!",
    disablePrevButton: true,
    disableInteraction: true,
    highlightedSelectors: ['*'],
  },
  {
    uid: '7LNXMXP',
    selector: '#step-profile-dropdown',
    content:
      'Clicking on the username gives you access to the settings and also allows you to restart this demo after closing or finishing it.',
    disableInteraction: true,
  },
  {
    uid: 'QJNtbat',
    selector: '#step-dashboard',
    content:
      'The "Dashboard" button is always available in the top bar and brings you back to this screen.',
    disableInteraction: true,
  },
  {
    uid: 'ACTWbyI',
    selector: '#step-support',
    content:
      'This button opens up your email program so that you can contact us in case you encounter any issues, or you have any questions.',
    disableInteraction: true,
  },
  {
    uid: 'DA5VzI8',
    selector: '#step-manual',
    content:
      'This link takes you to our FW Resources page, where you can find several useful materials on how to use our technology.',
    disableInteraction: true,
  },
  {
    uid: '3GXnbok',
    selector: '#step-wine-fermentation-tracking',
    content: 'The Dashboard is divided into "Fermentation Tracking"',
    disableInteraction: true,
  },
  {
    uid: '6j9mhLb',
    selector: '#step-wine-single-measurements”',
    content: 'and "Single Analysis"',
    disableInteraction: true,
  },
  {
    uid: 'tX6eJ0i',
    selector: '#step-wine-new-analysis',
    content:
      'First, we will walk you through how a single measurement is created. For this click on "Start New Analysis".',
    disableNextButton: true,
    position: 'bottom',
  },
  {
    uid: 'RLqQcrK',
    selector: '#step-new-viab-conz-create',
    resizeObservables: ['#step-new-viab-conz-create'],
    content:
      'Here, you can choose the type of measurement you wish to do. We will do a Viability and Concentration measurement so please click on "Viability and Concentration"',
    disablePrevButton: true,
    disableNextButton: true,
    padding: 30,
  },
  {
    uid: 'hxc1fUV',
    selector: '#step-camera-selection',
    resizeObservables: ['#step-camera-selection'],
    content:
      'This is the part where you normally need to have a microscope connected. You would then select the microscope camera in this dropdown.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'j5r7kRP',
    selector: '#step-image-upload-preview',
    content:
      'For the tutorial, however, we have already taken care of the image capture and provided you with 5 images.',
    disableInteraction: true,
  },
  {
    uid: 'whAJdvL',
    selector: '#step-information-on-sample',
    content: 'Next, we can add some information about the sample.',
    disableInteraction: true,
  },
  {
    uid: 'cBPkqlj',
    selector: '#step-sample-dilution',
    content:
      'Here, you put how the sample was diluted. If it was a yeast slurry, for example, you probably had to dilute 1 part sample with 99 parts water, so we have entered "99" for water. Feel free to edit these values.',
  },
  {
    uid: 'pEd7eqZ',
    selector: '#step-dilution-sample',
    content:
      'The diluted sample is almost always mixed in equal parts with the stain. So, you can leave the default values of 1 & 1 for the ratio here.',
  },
  {
    uid: 'QhxTv0P',
    selector: '#step-batch-name',
    content:
      'Optionally, you can give your sample a name, such as "Demo measurement" in this case.',
  },
  {
    uid: 'GBQNSFY',
    selector: '#step-density',
    content:
      'Here you can add the sugar content in the desired unit. The unit can be changed in the App Settings.',
  },
  {
    uid: 'kTNHvYp',
    selector: '#step-temperature',
    content: 'Here you can add the previously determined fermentation temperature.',
  },
  {
    uid: '1mddq44',
    selector: '#step-batch-description',
    content: 'and add any other notes you wish to save with your measurement.',
  },
  {
    uid: 'eBHmWhV',
    selector: '#step-batch-create-btn',
    content: 'Once all of this is done press "Analyze" to start the analysis.',
    disableNextButton: true,
  },
  {
    uid: '51oEgCY',
    selector: '#step-analysis-creation-done',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      "Just like that, we have the results of the analysis. That was easy, wasn't it?",
    disablePrevButton: true,
    position: 'center',
  },
  {
    uid: 'XCNgAve',
    selector: '#step-concentration',
    content:
      'Here, you see the concentration. Note, that this is the concentration before dilution (That is why we had to tell our app how much the sample was diluted)',
  },
  {
    uid: 'GMJl9x9',
    selector: '#step-budding',
    content:
      'This is the budding index. A budding index of 100% would mean that all cells are involved in a division (so either a mother or a daughter cell)',
  },
  {
    uid: 'lWhCkNk',
    selector: '#step-viability',
    content:
      'This value indicates the viability, so the percentage of cells that are alive. For pitching, this value should be above 90 or even better 95%',
  },
  {
    uid: 'nuMB1Bb',
    selector: '#step-analysis-general-info',
    content: 'Some general info about the analysis is shown here.',
  },
  {
    uid: 'QNNxXe8',
    selector: '#step-histogram',
    content:
      'There is also a histogram of the sizes of all yeast detected. Advanced users can use this to estimate the age of the population (as yeast gets larger with each division), or even to see if there are different strains of yeast present.',
  },
  {
    uid: 'miioOSA',
    selector: '.step-individual-image',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      'Finally, you can also review each individual image and see how it was analyzed. Click on the image to see a more detailed view.',
    disableNextButton: true,
  },
  {
    uid: 'yZgJOSQ',
    selector: '.step-image-comparison',
    resizeObservables: ['.step-image-comparison'],
    highlightedSelectors: ['step-image-comparison'],
    content:
      'If you move the slider left and right, you can compare the original image and the result of the automatic analysis.',
    disablePrevButton: true,
  },
  {
    uid: 'OJsmruk',
    selector: '.step-image-comparison',
    content:
      'Note, that cells touching white borders are always counted, and cells touching red borders are never counted. Just like how it works with the edges of a hemocytometer.',
  },
  {
    uid: 'WjZ6cqa',
    selector: '#step-edit-cells-btn',
    content:
      'It is also possible to make some manual corrections (such as adding or removing cells or changing classification), in case that is ever needed. Results would then of course be recomputed after making a change. Manual corrections are normally not needed. ',
    disableInteraction: true,
  },
  {
    uid: 'MlOWXEI',
    selector: '#step-edit-analysis',
    content:
      'In case you spot a mistake with the data entered for the analysis, such as entering the wrong dilution, you can click edit here to change this. The results would then also be computed again.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BfEDQcB',
    selector: '#step-download-pdf',
    resizeObservables: ['#step-download-pdf'],
    content: 'You can download a pdf report of this analysis.',
    disableInteraction: true,
  },
  {
    uid: 'venUIAF',
    selector: '#step-export-csv',
    content:
      'or export the full data as CSV so you can work with it in Excel for example.',
    disableInteraction: true,
  },
  {
    uid: 'DlEHbdj',
    selector: '#step-pitch-rate-btn',
    content:
      'Finally, now that we have the results, we can also calculate the pitch rate for a new batch by clicking on the "Pitch Calculator".',
    disableNextButton: true,
  },
  {
    uid: 'gOTK8sl',
    selector: '#step-pitch-settings-btn',
    resizeObservables: ['#step-pitch-settings-btn'],
    content:
      'The preferred type of units for the pitch calculator can be changed in the settings. We will stick with the defaults for now.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BA1Qa3z',
    selector: '#step-wort-volume',
    content:
      "Now, let's get started. The volume for the wine you will be making should be input here. We have entered 20 hl as an example.",
  },
  {
    uid: 'zofpyzw',
    selector: '#step-target-concentration',
    content:
      "Next you need to input the target concentration for the initial inoculation of your wine. As an example, we've set it at 2 million cells per milliliter.",
  },
  {
    uid: 'jaHrRaW',
    selector: '#step-pitch-rate-calc-btn',
    content:
      'After clicking "Calculate" you will get the exact volume or weight you should use to pitch.',
  },
  {
    uid: 'dzsFe6t',
    selector: '#step-dashboard',
    content:
      'Now let\'s go back to the Dashboard to show you some other options. Please click the "Dashboard" button.',
    disableNextButton: true,
  },
  {
    uid: 'luZY2o3',
    selector: '#step-wine-analysis-history-btn',
    content: 'click on "History Of Analysis"',
    disablePrevButton: true,
    disableNextButton: true,
  },
  {
    uid: 'GxV3pF1',
    selector: '#step-measurement-table',
    content:
      'This overview shows you all the analyses of this account, including the one we just did.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'O2KTUsd',
    selector: '#step-measurement-table',
    content:
      'Most users only see their own analyses. But special admin accounts can review measurements made by several users. This way a QA manager can review results from several sites for example.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: '8umJT2v',
    selector: '#step-measurement-table',
    content:
      'Clicking on any analysis will take you to a result screen just like what we saw before.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'Dm7QIsT',
    selector: '#step-dashboard',
    content: 'Lets go back to the Dashboard for now.',
    disableNextButton: true,
  },
  {
    uid: 'wuwV2GN',
    selector: '#step-wine-fermentation-tracking',
    content: "Now let's look at fermentation tracking.",
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: '7MTHe2U',
    selector: '#step-wine-fermentation-tracking',
    content:
      'If you want to measure yeast not as a one-off for pitching but instead to monitor a fermentation over time you can use this feature.',
    disableInteraction: true,
  },
  {
    uid: 'H51Pxou',
    selector: '#step-wine-fermentation-tracking',
    content:
      'Tracked fermentations can give you valuable insights and allows you to notice any sudden drops in viability or yeast growth. This is particularly useful for preventing stuck fermentation.',
    disableInteraction: true,
  },
  {
    uid: 'GI0ZWgu',
    selector: '#step-wine-tracking-overview-btn',
    content:
      'Each measurement here is much the same as what we just did for a "Single Analysis". To see how this works let\'s go to the overview.',
    disableNextButton: true,
  },
  {
    uid: 'OkjY32s',
    selector: '#step-fermentation-overview',
    content:
      "We have prepared a completed fermentation for the demo, so let's open that now.",
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'prahZDR',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    content:
      'As you can see here, measurements over many days are combined into a graph so you can see how your fermentation progresses.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'iRFDckB',
    selector: '#step-fermentation-details',
    content:
      'Here you can review the details entered when initially creating a fermentation',
    disableInteraction: true,
    position: 'top',
  },
  {
    uid: 'UiDnvMp',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    mutationObservables: ['#step-fermentation-graph'],
    content:
      'and a timeline summarizing all the measurement data. You can modify the display of the graph in several ways:',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'etjvWdB',
    selector: '#step-fermentation-graph-list',
    content:
      'Single measurements can be excluded by clicking the checkboxes. Please test this now.',
    disableInteraction: false,
  },
  {
    uid: 'FBsyTDN',
    selector: '#step-fermentation-graph',
    content:
      'Individual lines in the graph can be turned on and off by clicking on the labels in the legend at the bottom. Please test this now.',
    disableInteraction: false,
    position: 'bottom',
  },
  {
    uid: 'mZKsxky',
    selector: '#step-fermentation-single-analysis',
    content:
      'Below the graph, the detailed results for each measurement that makes up a fermentation are displayed.',
    position: 'center',
    disableInteraction: true,
  },
  {
    uid: '7iC88l2',
    selector: '#step-fermentation-tabs',
    content:
      'By selecting the tabs here you can switch the results of which measurement is shown.',
  },
  {
    uid: 'BWbcFaA',
    selector: '#step-dashboard',
    content:
      "This should cover the basic knowledge about tracked fermentations, let's go back to the Dashboard.",
    disableInteraction: false,
    disableNextButton: true,
  },
  {
    uid: '3dIOlms',
    selector: '#step-wine-dashboard',
    content:
      "This concludes the guided tutorial and it has now been disabled (you can click on the username and restart it at any time). Feel free to continue to explore the web app on your own. If you have any questions don't hesitate to get in touch!",
    disableInteraction: true,
    disablePrevButton: true,
    disableNextButton: true,
    highlightedSelectors: ['*'],
    position: 'top',
  },
];

export const ethanolSteps: TourStep[] = [
  {
    uid: 'DeElspw',
    selector: '#step-wine-dashboard',
    content:
      "Welcome! We'll walk you through the steps for using our WebApp. Let's dive in!",
    disablePrevButton: true,
    disableInteraction: true,
    highlightedSelectors: ['*'],
  },
  {
    uid: '7LNXMXP',
    selector: '#step-profile-dropdown',
    content:
      'Clicking on the username gives you access to the settings and also allows you to restart this demo after closing or finishing it.',
    disableInteraction: true,
  },
  {
    uid: 'QJNtbat',
    selector: '#step-dashboard',
    content:
      'The "Dashboard" button is always available in the top bar and brings you back to this screen.',
    disableInteraction: true,
  },
  {
    uid: 'ACTWbyI',
    selector: '#step-support',
    content:
      'This button opens up your email program so that you can contact us in case you encounter any issues, or you have any questions.',
    disableInteraction: true,
  },
  {
    uid: 'DA5VzI8',
    selector: '#step-manual',
    content:
      'This link takes you to our Resources page, where you can find several useful materials on how to use our technology.',
    disableInteraction: true,
  },
  {
    uid: '3GXnbok',
    selector: '#step-wine-fermentation-tracking',
    content: 'The Dashboard is divided into "Fermentation Tracking"',
    disableInteraction: true,
  },
  {
    uid: '6j9mhLb',
    selector: '#step-wine-single-measurements”',
    content: 'and "Single Analysis"',
    disableInteraction: true,
  },
  {
    uid: 'tX6eJ0i',
    selector: '#step-wine-new-analysis',
    content:
      'First, we will walk you through how a single measurement is created. For this click on "Start New Analysis".',
    disableNextButton: true,
    position: 'bottom',
  },
  {
    uid: 'RLqQcrK',
    selector: '#step-new-viab-conz-create',
    resizeObservables: ['#step-new-viab-conz-create'],
    content:
      'Here, you can choose the type of measurement you wish to do. We will do a Viability and Concentration measurement so please click on "Viability and Concentration"',
    disablePrevButton: true,
    disableNextButton: true,
    padding: 30,
  },
  {
    uid: 'hxc1fUV',
    selector: '#step-camera-selection',
    resizeObservables: ['#step-camera-selection'],
    content:
      'This is the part where you normally need to have a microscope connected. You would then select the microscope camera in this dropdown.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'j5r7kRP',
    selector: '#step-image-upload-preview',
    content:
      'For the tutorial, however, we have already taken care of the image capture and provided you with 5 images.',
    disableInteraction: true,
  },
  {
    uid: 'whAJdvL',
    selector: '#step-information-on-sample',
    content: 'Next, we can add some information about the sample.',
    disableInteraction: true,
  },
  {
    uid: 'cBPkqlj',
    selector: '#step-sample-dilution',
    content:
      'Here, you put how the sample was diluted. If it was a yeast slurry, for example, you probably had to dilute 1 part sample with 99 parts water, so we have entered "99" for water. Feel free to edit these values.',
  },
  {
    uid: 'pEd7eqZ',
    selector: '#step-dilution-sample',
    content:
      'The diluted sample is almost always mixed in equal parts with the stain. So, you can leave the default values of 1 & 1 for the ratio here.',
  },
  {
    uid: 'QhxTv0P',
    selector: '#step-batch-name',
    content:
      'Optionally, you can give your sample a name, such as "Demo measurement" in this case.',
  },
  {
    uid: '1mddq44',
    selector: '#step-batch-description',
    content: 'and add any other notes you wish to save with your measurement.',
  },
  {
    uid: 'eBHmWhV',
    selector: '#step-batch-create-btn',
    content: 'Once all of this is done press "Analyze" to start the analysis.',
    disableNextButton: true,
  },
  {
    uid: '51oEgCY',
    selector: '#step-analysis-creation-done',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      "Just like that, we have the results of the analysis. That was easy, wasn't it?",
    disablePrevButton: true,
    position: 'center',
  },
  {
    uid: 'XCNgAve',
    selector: '#step-concentration',
    content:
      'Here, you see the concentration. Note, that this is the concentration before dilution (That is why we had to tell our app how much the sample was diluted)',
  },
  {
    uid: 'GMJl9x9',
    selector: '#step-budding',
    content:
      'This is the budding index. A budding index of 100% would mean that all cells are involved in a division (so either a mother or a daughter cell)',
  },
  {
    uid: 'lWhCkNk',
    selector: '#step-viability',
    content:
      'This value indicates the viability, so the percentage of cells that are alive.',
  },
  {
    uid: 'nuMB1Bb',
    selector: '#step-analysis-general-info',
    content: 'Some general info about the analysis is shown here.',
  },
  {
    uid: 'QNNxXe8',
    selector: '#step-histogram',
    content:
      'There is also a histogram of the sizes of all yeast detected. Advanced users can use this to estimate the age of the population (as yeast gets larger with each division), or even to see if there are different strains of yeast present.',
  },
  {
    uid: 'miioOSA',
    selector: '.step-individual-image',
    resizeObservables: ['#step-analysis-creation-done'],
    content:
      'Finally, you can also review each individual image and see how it was analyzed. Click on the image to see a more detailed view.',
    disableNextButton: true,
  },
  {
    uid: 'yZgJOSQ',
    selector: '.step-image-comparison',
    resizeObservables: ['.step-image-comparison'],
    highlightedSelectors: ['step-image-comparison'],
    content:
      'If you move the slider left and right, you can compare the original image and the result of the automatic analysis.',
    disablePrevButton: true,
  },
  {
    uid: 'OJsmruk',
    selector: '.step-image-comparison',
    content:
      'Note, that cells touching white borders are always counted, and cells touching red borders are never counted. Just like how it works with the edges of a hemocytometer.',
  },
  {
    uid: 'WjZ6cqa',
    selector: '#step-edit-cells-btn',
    content:
      'It is also possible to make some manual corrections (such as adding or removing cells or changing classification), in case that is ever needed. Results would then of course be recomputed after making a change. Manual corrections are normally not needed. ',
    disableInteraction: true,
  },
  {
    uid: 'MlOWXEI',
    selector: '#step-edit-analysis',
    content:
      'In case you spot a mistake with the data entered for the analysis, such as entering the wrong dilution, you can click edit here to change this. The results would then also be computed again.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BfEDQcB',
    selector: '#step-download-pdf',
    resizeObservables: ['#step-download-pdf'],
    content: 'You can download a pdf report of this analysis.',
    disableInteraction: true,
  },
  {
    uid: 'venUIAF',
    selector: '#step-export-csv',
    content:
      'or export the full data as CSV so you can work with it in Excel for example.',
    disableInteraction: true,
  },
  {
    uid: 'DlEHbdj',
    selector: '#step-pitch-rate-btn',
    content:
      'Finally, now that we have the results, we can also calculate the pitch rate for a new batch by clicking on the "Pitch Calculator".',
    disableNextButton: true,
  },
  {
    uid: 'gOTK8sl',
    selector: '#step-pitch-settings-btn',
    resizeObservables: ['#step-pitch-settings-btn'],
    content:
      'The preferred type of units for the pitch calculator can be changed in the settings. We will stick with the defaults for now.',
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: 'BA1Qa3z',
    selector: '#step-wort-volume',
    content:
      "Now, let's get started. The volume for the batch you will be making should be input here. We have entered 20 hl as an example.",
  },
  {
    uid: 'zofpyzw',
    selector: '#step-target-concentration',
    content:
      "Next you need to input the target concentration for the initial inoculation. As an example, we've set it at 2 million cells per milliliter.",
  },
  {
    uid: 'jaHrRaW',
    selector: '#step-pitch-rate-calc-btn',
    content:
      'After clicking "Calculate" you will get the exact volume or weight you should use to pitch.',
  },
  {
    uid: 'dzsFe6t',
    selector: '#step-dashboard',
    content:
      'Now let\'s go back to the Dashboard to show you some other options. Please click the "Dashboard" button.',
    disableNextButton: true,
  },
  {
    uid: 'luZY2o3',
    selector: '#step-wine-analysis-history-btn',
    content: 'click on "History Of Analysis"',
    disablePrevButton: true,
    disableNextButton: true,
  },
  {
    uid: 'GxV3pF1',
    selector: '#step-measurement-table',
    content:
      'This overview shows you all the analyses of this account, including the one we just did.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'O2KTUsd',
    selector: '#step-measurement-table',
    content:
      'Most users only see their own analyses. But special admin accounts can review measurements made by several users. This way a QA manager can review results from several sites for example.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: '8umJT2v',
    selector: '#step-measurement-table',
    content:
      'Clicking on any analysis will take you to a result screen just like what we saw before.',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'Dm7QIsT',
    selector: '#step-dashboard',
    content: 'Lets go back to the Dashboard for now.',
    disableNextButton: true,
  },
  {
    uid: 'wuwV2GN',
    selector: '#step-wine-fermentation-tracking',
    content: "Now let's look at fermentation tracking.",
    disablePrevButton: true,
    disableInteraction: true,
  },
  {
    uid: '7MTHe2U',
    selector: '#step-wine-fermentation-tracking',
    content:
      'If you want to measure yeast not as a one-off for pitching but instead to monitor a fermentation over time you can use this feature.',
    disableInteraction: true,
  },
  {
    uid: 'H51Pxou',
    selector: '#step-wine-fermentation-tracking',
    content:
      'Tracked fermentations can give you valuable insights and allows you to notice any sudden drops in viability or yeast growth. This is particularly useful for preventing stuck fermentation.',
    disableInteraction: true,
  },
  {
    uid: 'GI0ZWgu',
    selector: '#step-wine-tracking-overview-btn',
    content:
      'Each measurement here is much the same as what we just did for a "Single Analysis". To see how this works let\'s go to the overview.',
    disableNextButton: true,
  },
  {
    uid: 'OkjY32s',
    selector: '#step-fermentation-overview',
    content:
      "We have prepared a completed fermentation for the demo, so let's open that now.",
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'prahZDR',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    content:
      'As you can see here, measurements over many days are combined into a graph so you can see how your fermentation progresses.',
    disablePrevButton: true,
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'iRFDckB',
    selector: '#step-fermentation-details',
    content:
      'Here you can review the details entered when initially creating a fermentation',
    disableInteraction: true,
    position: 'top',
  },
  {
    uid: 'UiDnvMp',
    selector: '#step-fermentation-graph',
    resizeObservables: ['#step-fermentation-graph'],
    mutationObservables: ['#step-fermentation-graph'],
    content:
      'and a timeline summarizing all the measurement data. You can modify the display of the graph in several ways:',
    disableInteraction: true,
    position: 'center',
  },
  {
    uid: 'etjvWdB',
    selector: '#step-fermentation-graph-list',
    content:
      'Single measurements can be excluded by clicking the checkboxes. Please test this now.',
    disableInteraction: false,
  },
  {
    uid: 'FBsyTDN',
    selector: '#step-fermentation-graph',
    content:
      'Individual lines in the graph can be turned on and off by clicking on the labels in the legend at the bottom. Please test this now.',
    disableInteraction: false,
    position: 'bottom',
  },
  {
    uid: 'mZKsxky',
    selector: '#step-fermentation-single-analysis',
    content:
      'Below the graph, the detailed results for each measurement that makes up a fermentation are displayed.',
    position: 'center',
    disableInteraction: true,
  },
  {
    uid: '7iC88l2',
    selector: '#step-fermentation-tabs',
    content:
      'By selecting the tabs here you can switch the results of which measurement is shown.',
  },
  {
    uid: 'BWbcFaA',
    selector: '#step-dashboard',
    content:
      "This should cover the basic knowledge about tracked fermentations, let's go back to the Dashboard.",
    disableInteraction: false,
    disableNextButton: true,
  },
  {
    uid: '3dIOlms',
    selector: '#step-wine-dashboard',
    content:
      "This concludes the guided tutorial and it has now been disabled (you can click on the username and restart it at any time). Feel free to continue to explore the web app on your own. If you have any questions don't hesitate to get in touch!",
    disableInteraction: true,
    disablePrevButton: true,
    disableNextButton: true,
    highlightedSelectors: ['*'],
    position: 'top',
  },
];
